import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, PackagePlus } from 'lucide-react';
import { MINIMO_COMPLEMENTAR, usePedidoComplementar, type AlvoComplementar } from './usePedidoComplementar';

/** Molde do "Justificar permanência" (`JustificativaFilaDialog`): o chamado e o
 *  texto viram a justificativa da Triagem Pedido Complementar. */
export function PedidoComplementarDialog({ alvo, onClose }: { alvo: AlvoComplementar | null; onClose: () => void }) {
  const [chamado, setChamado] = useState('');
  const [texto, setTexto] = useState('');
  const { enviarAsync, isEnviando } = usePedidoComplementar();
  useEffect(() => { if (alvo) { setChamado(''); setTexto(''); } }, [alvo]);

  const faltam = MINIMO_COMPLEMENTAR - texto.trim().length;
  const podeEnviar = !!chamado.trim() && faltam <= 0 && !isEnviando;
  const enviar = async () => {
    if (!alvo) return;
    try {
      await enviarAsync({ alvo, chamado, texto });
      onClose();
    } catch { /* a mutation mostra o erro; mantém o texto */ }
  };
  const juntas = alvo?.linhas ?? [];

  return (
    <Dialog open={!!alvo} onOpenChange={o => { if (!o && !isEnviando) onClose(); }}>
      <DialogContent className="sm:max-w-[560px]" onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackagePlus className="h-5 w-5 text-primary" />Pedido complementar · linha {juntas[0]?.rotulo || 'do pedido'}
          </DialogTitle>
          <DialogDescription>
            {alvo?.razaoSocial} · pedido {alvo?.numeroPedido}{alvo?.cotacao ? ` · cotação ${alvo.cotacao}` : ''}
            <br />
            Abra o chamado na Claro e registre aqui. A cotação vai inteira para a Triagem Pedido Complementar, e o que você escrever vira a justificativa de lá.
          </DialogDescription>
        </DialogHeader>
        {juntas.length > 1 && <div className="rounded-lg border border-border p-3 text-xs">
          <p className="font-semibold">Vão juntas ({juntas.length} linhas{alvo?.cotacao ? ` da cotação ${alvo.cotacao}` : ''})</p>
          <p className="break-words text-muted-foreground">{juntas.map(l => l.rotulo || 'linha sem número').join(' · ')}</p>
        </div>}
        <div className="grid gap-1">
          <Label htmlFor="complementar-chamado" className="text-xs font-semibold">Número do chamado (obrigatório)</Label>
          <Input id="complementar-chamado" value={chamado} onChange={e => setChamado(e.target.value)} disabled={isEnviando}
            placeholder="PRD 12345678" className="h-9 text-xs" autoFocus />
        </div>
        <div className="grid gap-1">
          <Label htmlFor="complementar-texto" className="text-xs font-semibold">O que o complementar precisa fazer (obrigatório)</Label>
          <Textarea id="complementar-texto" value={texto} onChange={e => setTexto(e.target.value)} disabled={isEnviando} rows={3}
            placeholder="Ex.: linha em conflito na cotação, abri PRD pedindo cotação e bilhete novos." />
          <p className="text-xs text-muted-foreground">{faltam > 0 ? `Faltam ${faltam} caracteres.` : 'Pronto para enviar.'}</p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isEnviando}>Cancelar</Button>
          <Button onClick={() => void enviar()} disabled={!podeEnviar}>
            {isEnviando ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Enviando…</> : 'Enviar para o Complementar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
